import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle, Lock, Building2, Shield, Eye, EyeOff,
  ArrowRight, Loader2, Sparkles, AlertCircle, User, Users,
  Check, X, Wand2
} from 'lucide-react';
import apiClient from '../../api/client';
import { useI18n } from '../../i18n';

const API_BASE = process.env.REACT_APP_API_URL || '';

const AcceptInvitation = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // States
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'invalid', 'expired', 'already_accepted'
  const [success, setSuccess] = useState(false);
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);

  // Invitation data
  const [invitationData, setInvitationData] = useState(null);

  // Form data
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    match: password === confirmPassword && password.length > 0
  };

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  // Role labels
  const roleLabels = {
    'analyst': { label: 'Analyste', color: 'text-green-400', bg: 'bg-green-500/20' },
    'manager': { label: 'Manager', color: 'text-blue-400', bg: 'bg-blue-500/20' },
    'admin': { label: 'Administrateur', color: 'text-purple-400', bg: 'bg-purple-500/20' },
    'company_admin': { label: 'Admin Entreprise', color: 'text-purple-400', bg: 'bg-purple-500/20' },
    'super_admin': { label: 'Super Admin', color: 'text-red-400', bg: 'bg-red-500/20' },
    'viewer': { label: 'Lecteur', color: 'text-gray-400', bg: 'bg-gray-500/20' }
  };

  // Generate strong password function
  const generateStrongPassword = () => {
    const length = 16;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const special = '!@#$%^&*(),.?:{}|<>';

    // Ensure at least one of each type
    let password = '';
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];

    // Fill the rest with random characters
    const allChars = uppercase + lowercase + numbers + special;
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');

    // Set both password fields
    setPassword(password);
    setConfirmPassword(password);
    setShowPassword(true); // Show password so user can see it
  };

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError(t('acceptInvitation.missingToken'));
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/v1/auth/invitation/verify/${token}`);
        const data = await response.json();

        if (!response.ok) {
          // Handle different error types based on HTTP status code
          if (response.status === 400 && data.detail && data.detail.includes('already been accepted')) {
            setAlreadyAccepted(true);
            setErrorType('already_accepted');
          } else if (response.status === 410) {
            // 410 Gone = Expired invitation
            setErrorType('expired');
            setError(data.detail || 'This invitation has expired. Please request a new invitation.');
          } else if (response.status === 404) {
            // 404 Not Found = Invalid/unknown token
            setErrorType('invalid');
            setError(data.detail || 'This invitation link is invalid. Make sure you are using the latest link received.');
          } else {
            // Other errors
            setErrorType('invalid');
            setError(data.detail || 'Token invalide');
          }
          return;
        }

        setInvitationData(data);
        setFirstName(data.first_name || '');
        setLastName(data.last_name || '');
      } catch (err) {
        setErrorType('invalid');
        setError(err.message || 'Unable to verify the invitation. Check your internet connection.');
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/v1/auth/invitation/accept/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password,
          first_name: firstName,
          last_name: lastName
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'acceptation');
      }

      // Store tokens and redirect
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('user', JSON.stringify(data.user));

      setSuccess(true);

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-brand-green mx-auto mb-4" />
          <p className="text-gray-400">{t('acceptInvitation.verifying')}</p>
        </div>
      </div>
    );
  }

  // Already accepted state (positive message)
  if (alreadyAccepted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#111111] rounded-2xl border border-brand-green/30 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-brand-green/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-brand-green" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{t('acceptInvitation.alreadyActivatedTitle')}</h1>
          <p className="text-gray-400 mb-6">
            {t('acceptInvitation.alreadyActivatedDesc')}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-brand-green text-black font-semibold rounded-xl hover:bg-brand-green/90 transition-all whitespace-nowrap"
          >
            {t('acceptInvitation.login')}
          </button>
        </div>
      </div>
    );
  }

  // Error state (invalid or expired token)
  if (error && !invitationData) {
    const isExpired = errorType === 'expired';
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className={`max-w-md w-full bg-[#111111] rounded-2xl border ${isExpired ? 'border-orange-500/30' : 'border-red-500/30'} p-8 text-center`}>
          <div className={`w-16 h-16 rounded-full ${isExpired ? 'bg-orange-500/20' : 'bg-red-500/20'} flex items-center justify-center mx-auto mb-6`}>
            <AlertCircle className={`w-8 h-8 ${isExpired ? 'text-orange-400' : 'text-red-400'}`} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {isExpired ? t('acceptInvitation.invitationExpiredTitle') : t('acceptInvitation.invitationInvalidTitle')}
          </h1>
          <p className="text-gray-400 mb-4">{error}</p>
          <p className="text-gray-500 text-sm mb-6">
            {isExpired
              ? t('acceptInvitation.expiredHint')
              : t('acceptInvitation.invalidHint')
            }
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-3 bg-brand-green text-black font-semibold rounded-xl hover:bg-brand-green/90 transition-all whitespace-nowrap"
            >
              {t('acceptInvitation.login')}
            </button>
            <a
              href="mailto:support@sylink.fr?subject=Aide%20Invitation"
              className="text-brand-green hover:underline text-sm"
            >
              {t('acceptInvitation.contactSupport')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#111111] rounded-2xl border border-brand-green/30 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-brand-green/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-brand-green" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{t('acceptInvitation.welcomeTitle')}</h1>
          <p className="text-gray-400 mb-4">
            {t('acceptInvitation.accountActivated')}
          </p>
          <p className="text-brand-green text-sm">
            {t('acceptInvitation.redirecting')}
          </p>
        </div>
      </div>
    );
  }

  const role = roleLabels[invitationData?.role] || roleLabels.analyst;

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-brand-green mb-2">SYLink Pentest</h1>
          <p className="text-gray-400">Penetration Testing Platform</p>
        </div>

        {/* Main Card */}
        <div className="bg-[#111111] rounded-2xl border border-brand-green/30 overflow-hidden">
          {/* Invitation Info */}
          <div className="bg-gradient-to-r from-brand-green/10 to-transparent p-6 border-b border-gray-800">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-brand-green/20 flex items-center justify-center">
                <Users className="w-7 h-7 text-brand-green" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white">{t('acceptInvitation.joinTeamTitle')}</h2>
                <p className="text-gray-400 text-sm">
                  {invitationData?.invited_by} invites you to join
                  {invitationData?.company_name && (
                    <span className="text-white font-medium"> {invitationData.company_name}</span>
                  )}
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm">Email:</span>
                <span className="text-white text-sm">{invitationData?.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm">Role:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${role.bg} ${role.color}`}>
                  {role.label}
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div className="text-center mb-4">
              <Shield className="w-10 h-10 text-brand-green mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-white">Configurez votre compte</h3>
              <p className="text-gray-500 text-sm">Choose a secure password to access the platform</p>
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">First name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full bg-[#0a0a0a] border border-gray-800 rounded-xl pl-10 pr-4 py-3 text-white
                             focus:outline-none focus:border-brand-green/50 transition-colors"
                    placeholder="Your first name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nom</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-gray-800 rounded-xl px-4 py-3 text-white
                           focus:outline-none focus:border-brand-green/50 transition-colors"
                  placeholder="Votre nom"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-gray-800 rounded-xl pl-10 pr-24 py-3 text-white
                           focus:outline-none focus:border-brand-green/50 transition-colors"
                  placeholder="Create a secure password"
                  required
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={generateStrongPassword}
                    className="text-brand-green hover:text-brand-green/80 transition-colors"
                    title="Generate a strong password"
                  >
                    <Wand2 className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-500 hover:text-gray-300 transition-colors"
                    title={showPassword ? 'Masquer' : 'Afficher'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Confirmer le mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-gray-800 rounded-xl pl-10 pr-12 py-3 text-white
                           focus:outline-none focus:border-brand-green/50 transition-colors"
                  placeholder="Confirmez votre mot de passe"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password requirements */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                { key: 'length', label: '8 characters min' },
                { key: 'uppercase', label: 'Majuscule' },
                { key: 'lowercase', label: 'Minuscule' },
                { key: 'number', label: 'Chiffre' },
                { key: 'special', label: 'Special character' },
                { key: 'match', label: 'Mots de passe identiques' }
              ].map(({ key, label }) => (
                <div key={key} className={`flex items-center gap-2 ${passwordChecks[key] ? 'text-brand-green' : 'text-gray-500'}`}>
                  {passwordChecks[key] ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  <span>{label}</span>
                </div>
              ))}
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={!isPasswordValid || submitting}
              className="w-full flex items-center justify-center gap-2 bg-brand-green text-black font-semibold
                       py-4 rounded-xl hover:bg-brand-green/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Activation en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Activer mon compte
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-600 text-sm mt-6">
          En activant votre compte, vous acceptez les{' '}
          <a href="/terms" className="text-brand-green hover:underline">conditions d'utilisation</a>
        </p>
      </div>
    </div>
  );
};

export default AcceptInvitation;
